import { TableCellComponentProps } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { BarProgress } from '@/modules/ProgressBar/components/organisms/atoms';

interface ProgressBarCellConfig {
  value: number;
  label?: string;
}

/**
 * Celda de tabla con barra de progreso
 * Reutiliza el componente BarProgress atómico
 */
export const ProgressBarCell = ({
  value,
  column,
  state = 'default',
  className,
  onCellFocus,
  stickyLeft = 0,
  isLastLocked = false,
  isRowEditing,
  hasColumnMaxWidthAuto = false,
}: TableCellComponentProps) => {
  const baseClasses = getTableCellClasses(
    state,
    className,
    column.locked,
    isLastLocked,
    undefined,
    isRowEditing,
    false,
    column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
  );
  const cellProps = getTableCellProps(column, stickyLeft, undefined, false, hasColumnMaxWidthAuto);

  const hasFixedWidth = column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto);
  const alignClass = getContentAlignClass(column.contentAlign);

  // El valor puede ser un número (0-100) o un objeto { value: number, label?: string }
  const isObject = value && typeof value === 'object' && !Array.isArray(value);
  const progressConfig = isObject ? (value as ProgressBarCellConfig) : null;
  const progress = progressConfig?.value ?? (typeof value === 'number' ? value : 0);
  const label = progressConfig?.label;
  const progressValue = typeof progress === 'number' ? Math.min(Math.max(progress, 0), 100) : 0;

  const isSelected = state === 'selected' && !isRowEditing;
  const textColor = 'ui-text-secondary';

  return (
    <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
      <div
        className={cn(
          'ui-flex ui-h-full ui-items-center',
          alignClass,
          hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden',
        )}
      >
        <div className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-gap-2">
          {/* Barra de progreso - reutiliza el componente atómico */}
          <BarProgress
            progress={progressValue}
            size="xxs"
            color="brand"
            selected={state === 'selected'}
            className="ui-flex-1"
          />

          {/* Porcentaje */}
          <div className="ui-flex ui-shrink-0 ui-items-center ui-gap-0.5">
            <span className={cn('ui-text-body-xs-regular ui-font-body-xs-medium', textColor)}>
              {label || Math.round(progressValue)}
            </span>
            {!label && (
              <span className={cn('ui-text-body-xs-regular ui-font-body-xs-medium', textColor)}>
                %
              </span>
            )}
          </div>
        </div>
      </div>
    </td>
  );
};
